import React, { useEffect, useState } from 'react';
import { Modal, Form, InputNumber, Input, notification, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { WLocation, AddLocation } from '@/types/inventoryV2';
import { addLocation, updateLocation } from '@/apis/inventoryv2';
import { getLocationIdx } from '@/utils/wLocation';
import SelectLocationType from '@/components/Selector/SelectLocationType';

interface LocationFormProps {
  detail?: WLocation;
  onClose: () => void;
  open: boolean;
  onDone: (location: WLocation) => void;
}

const LocationForm: React.FC<LocationFormProps> = ({
  detail,
  onClose,
  open,
  onDone
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AddLocation>();
  const [loading, setLoading] = useState(false);

  const l1Value = Form.useWatch('l1', form);
  const l2Value = Form.useWatch('l2', form);
  const l3Value = Form.useWatch('l3', form);
  const l4Value = Form.useWatch('l4', form);

  useEffect(() => {
    if (open) {
      if (detail) {
        form.setFieldsValue({
          l1: detail.l1,
          l2: detail.l2,
          l3: detail.l3,
          l4: detail.l4,
          l5: detail.l5,
          label1: detail.label1,
          label2: detail.label2,
          label3: detail.label3,
          label4: detail.label4,
          label5: detail.label5,
          desc: detail.desc,
          type: detail.type
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, detail, form]);

  const handleSubmit = async () => {
    const values: AddLocation = await form.validateFields();
    try {
      setLoading(true);

      let result: WLocation;
      if (detail) {
        result = await updateLocation(detail.id, values);
      } else {
        result = await addLocation(values);
      }

      notification.success({
        placement: 'top',
        message: detail ? t('common.editSuccess') : t('common.addSuccess'),
        description: detail
          ? t('warehouse.updateLocationSuccess')
          : t('warehouse.addLocationSuccess'),
        duration: 3,
      });

      onDone(result);
    } catch (error: any) {
      notification.error({
        placement: 'top',
        message: detail ? t('common.editError') : t('common.addError'),
        description: error === 72912 ? t('warehouse.locationIndexExsist', { index: getLocationIdx(values) }) : detail
          ? t('warehouse.updateLocationFailed')
          : t('warehouse.addLocationFailed'),
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={detail ? t('warehouse.editLocation') : t('warehouse.addLocation')}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t('common.confirm')}
      cancelText={t('common.cancel')}
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        size="small"
      >
        <Form.Item label={t('warehouse.locationIndex')}>
          <Form.Item
            name="l1"
            rules={[{ required: true, message: t('warehouse.l1Required') }]}
            style={{ marginBottom: 8 }}
          >
            <InputNumber changeOnWheel
              placeholder={t('warehouse.l1Required')}
              style={{ width: '100%' }}
              min={0}
              size="small"
            />
          </Form.Item>
          <Form.Item
            name="l2"
            dependencies={['l1']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value !== undefined && value !== null && getFieldValue('l1') === undefined) {
                    return Promise.reject(new Error(t('warehouse.l1RequiredFirst')));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            style={{ marginBottom: 8 }}
          >
            <InputNumber changeOnWheel
              placeholder="L2"
              style={{ width: '100%' }}
              min={0}
              size="small"
              disabled={l1Value === undefined || l1Value === null}
            />
          </Form.Item>
          <Form.Item
            name="l3"
            dependencies={['l2']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value !== undefined && value !== null) {
                    if (getFieldValue('l2') === undefined || getFieldValue('l2') === null) {
                      return Promise.reject(new Error(t('warehouse.l2RequiredFirst')));
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            style={{ marginBottom: 8 }}
          >
            <InputNumber changeOnWheel
              placeholder="L3"
              style={{ width: '100%' }}
              min={0}
              size="small"
              disabled={l1Value === undefined || l1Value === null || l2Value === undefined || l2Value === null}
            />
          </Form.Item>
          <Form.Item
            name="l4"
            dependencies={['l3']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value !== undefined && value !== null) {
                    if (getFieldValue('l3') === undefined || getFieldValue('l3') === null) {
                      return Promise.reject(new Error(t('warehouse.l3RequiredFirst')));
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            style={{ marginBottom: 8 }}
          >
            <InputNumber changeOnWheel
              placeholder="L4"
              style={{ width: '100%' }}
              min={0}
              size="small"
              disabled={l1Value === undefined || l1Value === null || l2Value === undefined || l2Value === null || l3Value === undefined || l3Value === null}
            />
          </Form.Item>
          <Form.Item
            name="l5"
            dependencies={['l4']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value !== undefined && value !== null) {
                    if (getFieldValue('l4') === undefined || getFieldValue('l4') === null) {
                      return Promise.reject(new Error(t('warehouse.l4RequiredFirst')));
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            style={{ marginBottom: 8 }}
          >
            <InputNumber changeOnWheel
              placeholder="L5"
              style={{ width: '100%' }}
              min={0}
              size="small"
              disabled={l1Value === undefined || l1Value === null || l2Value === undefined || l2Value === null || l3Value === undefined || l3Value === null || l4Value === undefined || l4Value === null}
            />
          </Form.Item>
        </Form.Item>

        <Form.Item label={t('warehouse.labels')}>
          <Form.Item name="label1" style={{ marginBottom: 8 }}>
            <Input placeholder={t('warehouse.label1')} size="small" />
          </Form.Item>
          <Form.Item name="label2" style={{ marginBottom: 8 }}>
            <Input placeholder={t('warehouse.label2')} size="small" />
          </Form.Item>
          <Form.Item name="label3" style={{ marginBottom: 8 }}>
            <Input placeholder={t('warehouse.label3')} size="small" />
          </Form.Item>
          <Form.Item name="label4" style={{ marginBottom: 8 }}>
            <Input placeholder={t('warehouse.label4')} size="small" />
          </Form.Item>
          <Form.Item name="label5" style={{ marginBottom: 8 }}>
            <Input placeholder={t('warehouse.label5')} size="small" />
          </Form.Item>
        </Form.Item>
        <Form.Item label={t('inventory.locationType')} name="type" initialValue={1} >
          <SelectLocationType />
        </Form.Item>

        <Form.Item
          label={t('common.desc')}
          name="desc"
        >
          <Input.TextArea
            placeholder={t('warehouse.locationDescPlaceholder')}
            rows={3}
            size="small"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LocationForm;